import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useWeather } from '../hooks/useWeather';
import TVLocationCard from '../components/TV/TVLocationCard';
import TVHeader from '../components/TV/TVHeader';
import TVForecast from '../components/TV/TVForecast';
import TVMap from '../components/TV/TVMap';
import TVAIWidget from '../components/TV/TVAIWidget';
import TVAlertBanner from '../components/TV/TVAlertBanner';
import TVSkeleton from '../components/TV/TVSkeleton';
import Icon from '../utils/icons';
import './TVDisplay.css';

function TVDisplay() {  
  const { locationIndex } = useParams();
  const {
    locations,
    weatherData,
    alertsData,
    aqiData,
    loading,
    error,
    lastUpdated,
    refresh
  } = useWeather(); // No initial locations - uses saved ones

  const [currentIndex, setCurrentIndex] = useState(0);
  const [cycleEnabled, setCycleEnabled] = useState(true);
  const [cycleInterval] = useState(15);
  const [viewMode, setViewMode] = useState('single');
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (locationIndex !== undefined) {
      const index = parseInt(locationIndex, 10);
      if (!isNaN(index) && index >= 0 && index < locations.length) {
        setCurrentIndex(index);
        setCycleEnabled(false);
      }
    }
  }, [locationIndex, locations.length]);

  useEffect(() => {
    if (!cycleEnabled || viewMode !== 'single' || locations.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % locations.length);
    }, cycleInterval * 1000);

    return () => clearInterval(interval);
  }, [cycleEnabled, cycleInterval, locations.length, viewMode]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowLeft':
          setCurrentIndex(prev => (prev - 1 + locations.length) % locations.length);
          setCycleEnabled(false);
          break;
        case 'ArrowRight':
          setCurrentIndex(prev => (prev + 1) % locations.length);
          setCycleEnabled(false);
          break;
        case ' ':
          e.preventDefault();
          setCycleEnabled(prev => !prev);
          break;
        case 'f':
          toggleFullscreen();
          break;
        case 'g':
          setViewMode(prev => prev === 'grid' ? 'single' : 'grid');
          break;
        case 'o':
          setViewMode(prev => prev === 'overview' ? 'single' : 'overview');
          break;
        case 'r':
          refresh();
          break;
        case 'Escape':
          if (document.fullscreenElement) {
            document.exitFullscreen();
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [locations.length, refresh, toggleFullscreen]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const currentLocation = locations[currentIndex];
  const currentWeather = currentLocation ? weatherData[currentLocation] : null;
  const currentAlerts = currentLocation ? (alertsData[currentLocation] || []) : [];
  
  // Collect all alerts across all locations for grid/overview modes
  const allAlerts = locations.reduce((acc, loc) => {
    const locAlerts = alertsData[loc] || [];
    return acc.concat(locAlerts);
  }, []);

  const getCoordinates = (weather) => {
    if (!weather) return null;
    if (weather.current?.coordinates) return weather.current.coordinates;
    if (weather.coordinates) return weather.coordinates;
    return null;
  };

  const currentCoordinates = getCoordinates(currentWeather);

  return (
    <div className="tv-display">
      {/* Header */}
      <TVHeader
        currentLocation={currentLocation}
        currentIndex={currentIndex}
        totalLocations={locations.length}
        lastUpdated={lastUpdated}
        cycleEnabled={cycleEnabled}
        viewMode={viewMode}
        onToggleCycle={() => setCycleEnabled(prev => !prev)}
        onToggleFullscreen={toggleFullscreen}
        onChangeView={setViewMode}
        isFullscreen={isFullscreen}
      />

      {/* Alert Banner — show for current location in single view, all locations otherwise */}
      <TVAlertBanner alerts={viewMode === 'single' ? currentAlerts : allAlerts} />

      {/* Main Content */}
      <main className="tv-main">
        {loading && Object.keys(weatherData).length === 0 ? (
          <TVSkeleton />
        ) : error ? (
          <div className="tv-error">
            <Icon name="alertTriangle" size={64} />
            <h2>Error Loading Data</h2>
            <p>{error}</p>
            <button onClick={refresh}>
              <Icon name="refresh" size={20} /> Retry
            </button>
          </div>
        ) : viewMode === 'single' ? (
          <div className="tv-single-view">
            {/* AI widget — thin, full-width banner at top */}
            <div className="tv-single-ai">
              <TVAIWidget activeLocation={currentLocation} />
            </div>

            {/* Content area: sidebar + main */}
            <div className="tv-single-content">
              {/* Left sidebar: location card */}
              <div className="tv-single-sidebar">
                <TVLocationCard
                  location={currentLocation}
                  data={currentWeather}
                  aqi={aqiData[currentLocation]}
                  size="medium"
                />
              </div>

              {/* Main area: map (biggest) + forecast below */}
              <div className="tv-single-main">
                <div className="tv-single-map">
                  <TVMap
                    coordinates={currentCoordinates}
                    location={currentLocation}
                    weatherData={currentWeather}
                  />
                </div>
                <div className="tv-single-forecast">
                  {currentWeather?.forecast ? (
                    <TVForecast data={currentWeather.forecast} compact />
                  ) : (
                    <div style={{ padding: '20px', color: 'white' }}>No forecast</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="tv-grid-view">
            {locations.map((location, index) => (
              <div key={location} className="tv-grid-item">
                <TVLocationCard
                  location={location}
                  data={weatherData[location]}
                  aqi={aqiData[location]}
                  size="medium"
                  isActive={index === currentIndex}
                  onClick={() => {
                    setCurrentIndex(index);
                    setViewMode('single');
                  }}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="tv-overview">
            <div className="tv-overview-grid">
              {locations.map((location, index) => {
                const locWeather = weatherData[location];
                const locCoords = getCoordinates(locWeather);
                return (
                  <div key={location} className="tv-overview-item">
                    <TVLocationCard
                      location={location}
                      data={locWeather}
                      aqi={aqiData[location]}
                      size="compact"
                      isActive={index === currentIndex}
                    />
                    <div className="tv-overview-map">
                      <TVMap
                        coordinates={locCoords}
                        location={location}
                        weatherData={locWeather}
                        compact
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>

      {/* Location Indicators */}
      {viewMode === 'single' && locations.length > 1 && (
        <div className="tv-indicators">
          {locations.map((loc, index) => (
            <button
              key={loc}
              className={`indicator ${index === currentIndex ? 'active' : ''}`}
              onClick={() => {
                setCurrentIndex(index);
                setCycleEnabled(false);
              }}
              title={loc}
            />
          ))}
        </div>
      )}

      {/* Controls Hint */}
      <div className="tv-controls-hint">
        <span><kbd>Space</kbd> Pause/Play</span>
        <span><kbd>←</kbd><kbd>→</kbd> Navigate</span>
        <span><kbd>F</kbd> Fullscreen</span>
        <span><kbd>G</kbd> Grid View</span>
        <span><kbd>O</kbd> Overview</span>
        <span><kbd>R</kbd> Refresh</span>
      </div>

      {/* Back Link */}
      {!isFullscreen && (
        <Link to="/" className="tv-back-link">
          <Icon name="chevronLeft" size={16} />
          <span>Back to Dashboard</span>
        </Link>
      )}

      {/* Progress Bar */}
      {cycleEnabled && viewMode === 'single' && locations.length > 1 && (
        <div className="tv-progress-bar">
          <div 
            className="tv-progress-fill"
            style={{ animationDuration: `${cycleInterval}s` }}
            key={currentIndex}
          />
        </div>
      )}
    </div>
  );
}

export default TVDisplay;
